import { mdiAlertCircle, mdiSend, mdiPaperclip } from '@mdi/js';
import { Formik, FormikHelpers as FormikActions, FormikProps, getIn, yupToFormErrors } from 'formik';
import _ from 'lodash';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
import { MessageDropZone } from '..';
import { MessagePurposeType } from '../../../api';
import { MessagingContext } from '../../../services/messaging/context';
import { isSupport, Tooltip } from '../../base';
import { validateLatin1String } from '../../../utils';
import * as S from './chat-controls.styles';
import { SupportMessage } from '../../../api/server/models/SupportMessage';

interface IChatComment {
    note: boolean;
    comment: string;
    attachments: File[];
}

interface IChatControlsProps {
    note?: boolean;
    hideNote?: boolean;
    hideDone?: boolean;
}

export const ChatControls: React.FunctionComponent<IChatControlsProps> = (
    props: IChatControlsProps,
): React.ReactElement => {
    const context = useContext(MessagingContext);

    const {
        isReadOnly,
        message,
        providerMessage,
        enableMessageTypeValidation,
        onComposeComment,
        onMarkAsClosed,
        onMarkAsOpen,
        onProviderMarkAsClosed,
        onProviderMarkAsOpen,
        onMessageClose,
    } = context;
    const initial: IChatComment = {
        note: !!props.note || !props.hideNote,
        comment: '',
        attachments: [],
    };
    const attachInput = useRef<HTMLInputElement | null>(null);
    const [rows, setRows] = useState(1);
    const [locked, setLocked] = useState(false);
    const [latin1ValidationError, setLatin1ValidationError] = useState<string | null>(null);
    const [pendingMessageStatus, setPendingMessageStatus] = useState<'close' | 'open' | null>(null);
    const [pendingProviderStatus, setPendingProviderStatus] = useState<'close' | 'open' | null>(null);

    useEffect(() => {
        setPendingMessageStatus(null);
        setPendingProviderStatus(null);
    }, [message?.id]);

    const setPendingDone = (value: boolean): void => {
        setPendingMessageStatus(value ? 'close' : 'open');
    };
    const setPendingProviderDone = (value: boolean): void => {
        setPendingProviderStatus(value ? 'close' : 'open');
    };

    const handleAttachFile = (): void => {
        if (attachInput.current) {
            attachInput.current.click();
        }
    };
    const handleFileChange = (
        formik: FormikProps<IChatComment>,
    ): ((e: React.ChangeEvent<HTMLInputElement>) => void) => {
        return (e: React.ChangeEvent<HTMLInputElement>): void => {
            const { attachments } = formik.values;
            const files = _.map(e.target.files, file => file);
            formik.setFieldValue('attachments', _.concat(attachments, files));
        };
    };
    const handleRemoveAttachment = (formik: FormikProps<IChatComment>, attachment: object): (() => void) => {
        return (): void => {
            const { attachments } = formik.values;
            formik.setFieldValue('attachments', _.without(attachments, attachment));
            // NB: force reset so file is removed
            if (attachInput.current) {
                attachInput.current.value = '';
            }
        };
    };
    const handleChangeComment = (
        formik: FormikProps<IChatComment>,
    ): ((e: React.ChangeEvent<HTMLTextAreaElement>) => void) => {
        return (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
            const newComment = e.target.value;

            formik.setFieldValue('comment', newComment);
            const multiRow = _.size(newComment) >= 40 || _.includes(newComment, '\n');
            setRows(multiRow ? 8 : 1);

            // Clear validation error when user starts typing
            if (latin1ValidationError) {
                setLatin1ValidationError(null);
            }
        };
    };

    const onChangeNote = (formik: FormikProps<IChatComment>): ((value: boolean) => void) => {
        return (value: boolean): void => {
            formik.setFieldValue('note', value);
        };
    };

    const handleKeydown = (formik: FormikProps<IChatComment>): ((e: React.KeyboardEvent) => void) => {
        return (e: React.KeyboardEvent): void => {
            // if (e.key === 'Enter' && e.shiftKey) {
            //     formik.submitForm();
            // }
        };
    };

    const handleBlur = (formik: FormikProps<IChatComment>) => {
        return (e: React.FocusEvent<HTMLTextAreaElement>) => {
            formik.setFieldTouched('comment', true);

            // Validate on blur
            const value = e.target.value;
            const validationResult = validateLatin1String(value);
            if (!validationResult.isValid) {
                setLatin1ValidationError(validationResult.errorMessage || null);
            }
        };
    };

    const handlePaste = (
        formik: FormikProps<IChatComment>,
    ): ((e: React.ClipboardEvent<HTMLTextAreaElement>) => void) => {
        return (e: React.ClipboardEvent<HTMLTextAreaElement>): void => {
            const images = _.map(e.clipboardData.items, item => {
                if (item.type.startsWith('image/')) {
                    return item.getAsFile();
                }
            });
            if (images && images.length) {
                const { attachments } = formik.values;
                const newAttachments = _.compact(_.concat(attachments, images));
                formik.setFieldValue('attachments', newAttachments);
            }
        };
    };

    const handleSubmit = async (
        values: IChatComment,
        { setFieldValue }: FormikActions<IChatComment>,
    ): Promise<void> => {
        if (message && message.id) {
            const { comment, attachments, note } = values;
            setLocked(true);
            try {
                await (onComposeComment &&
                    onComposeComment({ messageId: message.id, body: comment, note, files: attachments }));
                setRows(1);
                setFieldValue('comment', '');
                setFieldValue('attachments', []);

                const hadMessagePending = pendingMessageStatus !== null;
                const hadProviderPending = pendingProviderStatus !== null;
                if (pendingMessageStatus === 'close') {
                    await onMarkAsClosed(message.id);
                    setPendingMessageStatus(null);
                } else if (pendingMessageStatus === 'open') {
                    await onMarkAsOpen(message.id);
                    setPendingMessageStatus(null);
                }
                if (pendingProviderStatus === 'close' && onProviderMarkAsClosed) {
                    await onProviderMarkAsClosed(message.id);
                    setPendingProviderStatus(null);
                } else if (pendingProviderStatus === 'open' && onProviderMarkAsOpen) {
                    await onProviderMarkAsOpen(message.id);
                    setPendingProviderStatus(null);
                }
                if (hadMessagePending || hadProviderPending) {
                    onMessageClose();
                }
            } catch (err) {
                console.log(err);
                throw err;
            } finally {
                setLocked(false);
            }
        }
    };
    const handleSave = (formik: FormikProps<IChatComment>): (() => Promise<void>) => {
        // NB: avoid non-async formik.submitForm for now
        return async (): Promise<void> => {
            const errors = await formik.validateForm();
            if (_.isEmpty(errors)) {
                await handleSubmit(formik.values, formik);
                formik.resetForm();
            }
        };
    };

    const generateValidationSchema = useCallback(() => {
        const shape = {
            comment: Yup.string().when('$attachments', (attachments: File[] | undefined, schema: any) => {
                if ((attachments || []).length > 0) {
                    return schema;
                }

                return schema
                    .required('Comment is required')
                    .test('not-empty', 'Comment cannot be empty', (value?: string) => {
                        return !value || value.trim().length > 0;
                    });
            }),
            attachments: Yup.array(),
        };

        if (enableMessageTypeValidation) {
            if (message?.purposeType === MessagePurposeType.PROVIDERDOCUMENTCOLLECTION) {
                shape.attachments = shape.attachments.min(1, 'Document is required for this type of message');
            }

            if (message?.purposeType === MessagePurposeType.PROVIDERNUMBERCOLLECTION) {
                shape.comment = shape.comment.test(
                    'comment',
                    'You must specify a number for this type of message',
                    value => !isNaN(Number(value)),
                );
            }
        }

        return Yup.object().shape(shape);
    }, [message?.purposeType]);

    const getComposePlaceholder = useCallback(() => {
        let placeholder = 'Type your message here';
        if (enableMessageTypeValidation) {
            if (message?.purposeType === MessagePurposeType.PROVIDERNUMBERCOLLECTION) {
                placeholder = 'Pleace specify a number';
            }
        }

        return placeholder;
    }, [message?.purposeType]);

    const getInputAccept = useCallback(() => {
        if (enableMessageTypeValidation) {
            if (message?.purposeType === MessagePurposeType.PROVIDERDOCUMENTCOLLECTION) {
                return 'application/pdf,.pdf,.xls,.xlsx,.csv,.doc,.docx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
            }
        }

        return;
    }, [message?.purposeType]);

    return (
        <Formik
            enableReinitialize={true}
            initialValues={initial}
            onSubmit={handleSubmit}
            validate={(values: IChatComment) => {
                const schema = generateValidationSchema();
                try {
                    schema.validateSync(values, { abortEarly: false, context: values });

                    return {};
                } catch (err) {
                    return yupToFormErrors(err);
                }
            }}>
            {(formik: FormikProps<IChatComment>): React.ReactElement => {
                const attached = _.map(
                    formik.values.attachments,
                    (attachment: File, i: number): React.ReactElement => (
                        <S.StyledAttachmentButton
                            key={`attachment-${i}`}
                            filename={attachment.name}
                            filesize={attachment.size}
                            onRemove={handleRemoveAttachment(formik, attachment)}
                        />
                    ),
                );

                const { errors, touched: formTouched } = formik;

                const commentError = getIn(errors, 'comment');
                const attachmentsError = getIn(errors, 'attachments');
                const touched = getIn(formTouched, 'comment');

                const error = attachmentsError || commentError;

                const errorMessage = touched && error ? error : undefined;

                if (isReadOnly) {
                    return <></>;
                }

                const placeholder = getComposePlaceholder();

                // Determine if we should show quick answer bubbles
                const categoryNames =
                    message?.links?.messageCategories
                        ?.map(cat => context.getCategoryName?.(cat.id))
                        .filter((name): name is string => !!name) || [];

                const hasNoOtherComments = context.comments && context.comments.length === 1;
                const isTextareaEmpty = !formik.values.comment || formik.values.comment.trim() === '';

                const quickAnswers: string[] = [];
                if (hasNoOtherComments && !locked && isTextareaEmpty) {
                    if (categoryNames.includes('Claim Halted'))
                        quickAnswers.push('RESUME CLAIM'); //Claim Halted
                    else if (categoryNames.includes('Eligibility Failed'))
                        quickAnswers.push('MOVE TO COLLECTIONS', 'RESUME CLAIM'); //FAILED_ELIGIBILITY_FIX
                    else if (categoryNames.some(name => name.includes('Inactive/Invalid Subscriber')))
                        quickAnswers.push('OVERRIDE_CLAIM_CHECK'); //CLAIM_SUBMIT_FAILED_ANALYZE
                }

                const handleQuickAnswerClick = (comment: string): void => {
                    formik.setFieldValue('comment', comment);

                    const multiRow = _.size(comment) >= 40 || _.includes(comment, '\n');
                    setRows(multiRow ? 8 : 1);
                };

                const handleFilesDrop = (files: File[]): void => {
                    if (locked) {
                        return;
                    }

                    const { attachments } = formik.values;
                    const newAttachments = _.concat(attachments, files);
                    formik.setFieldValue('attachments', newAttachments);
                };

                return (
                    <MessageDropZone disabled={locked} onFilesDrop={handleFilesDrop}>
                        {({ isDragging, containerProps }) => (
                            <S.Controls isNote={formik.values.note} {...containerProps}>
                                {isDragging && <S.DragOverlay>Drop files here to attach</S.DragOverlay>}
                                {quickAnswers.length > 0 && (
                                    <S.QuickAnswersContainer>
                                        {quickAnswers.map(answer => (
                                            <S.QuickAnswerBubble
                                                key={answer}
                                                onClick={() => handleQuickAnswerClick(answer)}>
                                                {answer}
                                            </S.QuickAnswerBubble>
                                        ))}
                                    </S.QuickAnswersContainer>
                                )}
                                <S.InputWrapper>
                                    <S.InputContainer>
                                        {!!touched && errorMessage && (
                                            <S.InputError>
                                                <S.InputErrorIcon path={mdiAlertCircle} />
                                                <S.InputErrorMessage>{errorMessage}</S.InputErrorMessage>
                                            </S.InputError>
                                        )}
                                        <S.InputBox locked={locked}>
                                            <S.Col>
                                                {attached && attached.length ? (
                                                    <S.InputAttachments>{attached}</S.InputAttachments>
                                                ) : null}
                                                <S.Textarea
                                                    rows={rows}
                                                    value={formik.values.comment}
                                                    onChange={handleChangeComment(formik)}
                                                    onKeyDown={handleKeydown(formik) as any}
                                                    onPaste={handlePaste(formik)}
                                                    onBlur={handleBlur(formik)}
                                                    placeholder={placeholder}
                                                    disabled={locked}
                                                    disableLatin1Validation={true}
                                                    errorMessage={latin1ValidationError || undefined}
                                                />
                                            </S.Col>
                                        </S.InputBox>
                                        <S.ActionsWrapper>
                                            <S.ActionsCol>
                                                <S.ActionsItem>
                                                    <S.AttachInput
                                                        ref={attachInput}
                                                        accept={
                                                            isSupport
                                                                ? getInputAccept()
                                                                : 'application/pdf,.pdf,.xls,.xlsx,.csv,.doc,.docx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                                                        }
                                                        type="file"
                                                        onChange={handleFileChange(formik)}
                                                    />
                                                    <Tooltip
                                                        direction="top"
                                                        title={
                                                            <span>
                                                                Upload file or drag and drop files here to attach
                                                            </span>
                                                        }>
                                                        <div>
                                                            <S.AttachButton
                                                                variant="outlined"
                                                                onClick={handleAttachFile}
                                                                disabled={locked}>
                                                                <S.AttachIcon path={mdiPaperclip} size={0.75} />
                                                            </S.AttachButton>
                                                        </div>
                                                    </Tooltip>
                                                </S.ActionsItem>
                                                {!props.hideNote && (
                                                    <S.ActionsItem>
                                                        <S.NoteToggleWrapper>
                                                            <S.NoteCheckbox
                                                                label="Internal"
                                                                disabled={locked}
                                                                checked={formik.values.note}
                                                                onChange={e => onChangeNote(formik)(e.target.checked)}
                                                            />
                                                        </S.NoteToggleWrapper>
                                                    </S.ActionsItem>
                                                )}
                                                {message && !props.hideDone && (
                                                    <S.ActionsItem>
                                                        <Tooltip
                                                            direction="top"
                                                            title={<span>Done after sending the comment</span>}>
                                                            <div>
                                                                <S.NoteToggleWrapper>
                                                                    <S.NoteCheckbox
                                                                        label="Done"
                                                                        disabled={locked}
                                                                        checked={
                                                                            pendingMessageStatus !== null
                                                                                ? pendingMessageStatus === 'close'
                                                                                : !message.open
                                                                        }
                                                                        onChange={e => {
                                                                            if (locked) return;
                                                                            setPendingDone(e.target.checked);
                                                                        }}
                                                                    />
                                                                </S.NoteToggleWrapper>
                                                            </div>
                                                        </Tooltip>
                                                    </S.ActionsItem>
                                                )}

                                                {!props.hideNote && (
                                                    <S.ActionsItem>
                                                        <Tooltip
                                                            direction="top"
                                                            title={
                                                                <span>Done as Provider after sending the comment</span>
                                                            }>
                                                            <div>
                                                                <S.NoteToggleWrapper>
                                                                    <S.NoteCheckbox
                                                                        label="Provider Done"
                                                                        disabled={locked}
                                                                        checked={
                                                                            pendingProviderStatus !== null
                                                                                ? pendingProviderStatus === 'close'
                                                                                : !(message as SupportMessage)
                                                                                      ?.providerOpen
                                                                        }
                                                                        onChange={e => {
                                                                            if (locked) return;
                                                                            setPendingProviderDone(e.target.checked);
                                                                        }}
                                                                    />
                                                                </S.NoteToggleWrapper>
                                                            </div>
                                                        </Tooltip>
                                                    </S.ActionsItem>
                                                )}
                                            </S.ActionsCol>
                                            <S.ActionsItem>
                                                <S.SendButton
                                                    variant="primary"
                                                    onClick={handleSave(formik)}
                                                    disabled={
                                                        locked ||
                                                        !!error ||
                                                        !!latin1ValidationError ||
                                                        (!formik.values.comment.trim() &&
                                                            formik.values.attachments.length === 0)
                                                    }>
                                                    <S.SendIcon path={mdiSend} size={0.75} />
                                                </S.SendButton>
                                            </S.ActionsItem>
                                        </S.ActionsWrapper>
                                    </S.InputContainer>
                                </S.InputWrapper>
                            </S.Controls>
                        )}
                    </MessageDropZone>
                );
            }}
        </Formik>
    );
};
